import CustomerPropertyPicker from '@/components/trades/CustomerPropertyPicker';
// Full-page Create Job workspace — /app/jobs/new. Reuses the existing POST /jobs API.
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AddressAutocomplete } from "@/components/AddressAutocomplete";
import { JobBillingSection, jobBillingSummary } from "@/components/JobBillingSection";
import { ArrowLeft, Search, X, Loader2, MapPin, Calendar, Users, DollarSign, Check, Briefcase } from "lucide-react";
import { toast } from "sonner";

const STATUS = [["planned", "Planned"], ["in_progress", "In progress"], ["on_hold", "On hold"], ["completed", "Completed"]];
const STATUS_LABEL = Object.fromEntries(STATUS);
const money = (n) => `$${Number(n).toLocaleString("en-NZ")}`;

export default function JobCreate({ trades = false }) {
  const nav = useNavigate();
  const { user } = useAuth();
  const [sp] = useSearchParams();
  const [clients, setClients] = useState([]);
  const [lookupError, setLookupError] = useState(false);
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({
    ...(trades ? { property_id: sp.get("property_id") || null } : {}),
    title: sp.get("title") || "", client_id: sp.get("client_id") || "", site_address: sp.get("address") || "",
    site_lat: null, site_lng: null, description: sp.get("scope") || "",
    status: "planned", start_date: "", end_date: "", assigned_user_ids: [],
    quoted_value: sp.get("quoted") || "", fleet_rate_overrides: {},
    billing_type: "fixed_price", charge_up_sources: { labour: true, plant: true, materials: true }, invoice_frequency: "manual",
  });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [leaveOpen, setLeaveOpen] = useState(false);
  const [crewQ, setCrewQ] = useState("");
  const [crewFilter, setCrewFilter] = useState("all");

  useEffect(() => { if (user.role === "field") nav("/app/jobs", { replace: true }); }, [user.role, nav]);
  useEffect(() => {
    if (trades) {
      const controller = new AbortController();
      api.get("/job-workspace/options?include_clients=false", { __flSkipReadCache: true, signal: controller.signal }).then(({ data }) => {
        if (controller.signal.aborted) return;
        if (data.org_id !== user.org_id) throw new Error("Workspace identity changed");
        setClients(data.clients); setUsers(data.users);
      }).catch(() => { if (!controller.signal.aborted) setLookupError(true); });
      return () => controller.abort();
    }
    api.get("/clients", { __flSkipReadCache: trades }).then((r) => setClients(r.data)).catch(() => { if (trades) setLookupError(true); });
    api.get("/users", { __flSkipReadCache: trades }).then((r) => setUsers(r.data)).catch(() => { if (trades) setLookupError(true); });
  }, []);
  useEffect(() => {
    const h = (e) => { if (dirty) { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [dirty]);

  const set = (k) => (e) => { setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e })); setDirty(true); setErrors((x) => ({ ...x, [k]: null })); };
  const toggleCrew = (uid) => {
    setForm((f) => ({ ...f, assigned_user_ids: f.assigned_user_ids.includes(uid) ? f.assigned_user_ids.filter((x) => x !== uid) : [...f.assigned_user_ids, uid] }));
    setDirty(true);
  };

  const duration = useMemo(() => {
    if (!form.start_date || !form.end_date) return null;
    const d = Math.round((new Date(form.end_date) - new Date(form.start_date)) / 86400000);
    return d >= 0 ? d + 1 : null;
  }, [form.start_date, form.end_date]);
  const dateError = form.start_date && form.end_date && new Date(form.end_date) < new Date(form.start_date);

  const crew = useMemo(() => {
    const q = crewQ.trim().toLowerCase();
    return users.filter((u) => {
      if (crewFilter === "field" && u.role !== "field") return false;
      if (crewFilter === "office" && !["office", "admin", "super_admin"].includes(u.role)) return false;
      return !q || `${u.name} ${u.role}`.toLowerCase().includes(q);
    });
  }, [users, crewQ, crewFilter]);
  const selectedUsers = users.filter((u) => form.assigned_user_ids.includes(u.id));
  const clientName = clients.find((c) => c.id === form.client_id)?.name;

  const checklist = [
    ["Job title", !!form.title.trim()],
    ["Travel billing chosen", typeof form.bill_travel_time === "boolean"],
    [trades ? "Customer" : "Client", !!form.client_id],
    ["Site address", !!form.site_address],
    [trades ? "Planned dates" : "Programme dates", !!form.start_date && !!form.end_date && !dateError],
    [trades ? "Job access team" : "Crew assigned", form.assigned_user_ids.length > 0],
  ];
  const pct = Math.round((checklist.filter(([, ok]) => ok).length / checklist.length) * 100);

  const create = async () => {
    if (trades && lookupError) { toast.error("Customer/team options are unavailable. Reload before creating the job."); return; }
    if (!form.title.trim()) { setErrors({ title: "Job title is required" }); toast.error("Job title is required"); return; }
    if (typeof form.bill_travel_time !== "boolean") { toast.error("Choose whether travel time is chargeable to the client"); document.querySelector('[data-testid="job-commercial-section"]')?.setAttribute("open", ""); document.querySelector('[data-testid="job-travel-billing"]')?.scrollIntoView({block: "center"}); return; }
    if (dateError) { toast.error("End date must be after the start date"); return; }
    setBusy(true);
    try {
      const payload = { ...form, client_id: form.client_id || null, quoted_value: form.quoted_value === "" ? null : Number(form.quoted_value) };
      const { data } = await api.post("/jobs", payload);
      setDirty(false);
      toast.success(`Job created — ${form.title.trim()}`);
      nav(data?.id ? `/app/jobs/${data.id}` : "/app/jobs");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create job");
    } finally {
      setBusy(false);
    }
  };
  const cancel = () => { if (dirty) setLeaveOpen(true); else nav("/app/jobs"); };

  const CommercialSection = trades ? "details" : "section";
  const SectionTag = ({ icon: Icon, children }) => (
    <div className="label-tag mb-3 flex items-center gap-1.5"><Icon className="w-3 h-3" /> {children}</div>
  );
  const Err = ({ msg }) => msg ? <p className="text-[11px] font-bold text-red-500 mt-1" role="alert">⚠ {msg}</p> : null;

  return (
    <div data-testid="job-create-page" className="w-full max-w-none pb-28">
      {/* Sticky header */}
      <div className="sticky top-0 z-30 bg-white dark:bg-black border-b-2 border-zinc-900 dark:border-zinc-700 px-4 lg:px-6 py-3">
        <div className="flex items-center gap-3 flex-wrap">
          <button onClick={cancel} className="text-zinc-500 hover:text-zinc-900 dark:hover:text-white p-1" data-testid="job-create-back"><ArrowLeft className="w-5 h-5" /></button>
          <div className="min-w-0">
            <div className="text-[10px] font-mono font-bold uppercase tracking-[0.25em] text-zinc-500">
              Work / <Link to="/app/jobs" className="hover:text-[#FF5F15]">Jobs</Link> / New job
            </div>
            <div className="font-display font-black uppercase tracking-tight text-lg truncate">Create new job</div>
          </div>
          <div className="ml-auto flex items-center gap-2">
            {dirty && <span className="hidden sm:block text-[10px] font-mono uppercase tracking-widest text-amber-600">Unsaved</span>}
            <Button variant="outline" onClick={cancel} className="rounded-sm uppercase tracking-wider font-bold text-xs border-zinc-400" data-testid="job-create-cancel">Cancel</Button>
            <Button onClick={create} disabled={busy} className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white uppercase tracking-wider font-bold text-xs" data-testid="job-create-submit">
              {busy ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Creating job…</> : "Create job"}
            </Button>
          </div>
        </div>
      </div>

      <div className="px-4 lg:px-6 py-5 grid lg:grid-cols-[1fr_340px] gap-5 items-start">
        {/* LEFT — form sections */}
        <div className={trades ? "flex flex-col gap-5 min-w-0" : "space-y-5 min-w-0"}>
          {trades && lookupError && <p role="alert" className="text-sm">Customer or team options could not be loaded. Reload this page before saving.</p>}
          <section className="hard-card p-5" data-testid="job-details-section" data-tour-id="job-details-section">
            <SectionTag icon={Briefcase}>Job details</SectionTag>
            <Label className="label-tag">Job title *</Label>
            <Input value={form.title} onChange={set("title")} placeholder={trades ? "Job name" : "e.g. Henderson culvert renewal"}
              className={`mt-1.5 rounded-sm ${errors.title ? "border-red-500" : "border-zinc-400"}`} data-testid="job-title-input" data-tour-id="job-title-input" />
            <Err msg={errors.title} />
            <div className="grid sm:grid-cols-2 gap-4 mt-4">
              <div>
                <Label className="label-tag">{trades ? "Customer" : "Client"}</Label>
                {trades ? <CustomerPropertyPicker form={form} onChange={patch => { setForm(f => ({ ...f, ...patch })); setDirty(true); }} /> : (
                <Select value={form.client_id} onValueChange={set("client_id")}>
                  <SelectTrigger className="mt-1.5 rounded-sm border-zinc-400" data-testid="job-client-select" data-tour-id="job-client-select"><SelectValue placeholder={trades ? "Select a customer…" : "Select a client…"} /></SelectTrigger>
                  <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>)}
              </div>
              <div>
                <Label className="label-tag">Status</Label>
                <Select value={form.status} onValueChange={set("status")}>
                  <SelectTrigger className="mt-1.5 rounded-sm border-zinc-400" data-testid="job-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="mt-4">
              <Label className="label-tag">Site address</Label>
              {trades && <p className="text-sm text-muted-foreground mt-1">Review the actual work location. The customer address may be different.</p>}
              <div className="mt-1.5">
                {trades && form.property_id ? <p className="py-3">{form.site_address || "Loading property address…"}</p> : <AddressAutocomplete value={form.site_address}
                  onChange={(v) => { setForm((f) => ({ ...f, site_address: v })); setDirty(true); }}
                  onSelect={({ address, lat, lng }) => { setForm((f) => ({ ...f, site_address: address ?? f.site_address, site_lat: lat ?? null, site_lng: lng ?? null })); setDirty(true); }}
                  placeholder="123 Lincoln Road, Auckland" className="rounded-sm border-zinc-400" testid="job-address-input" />}
              </div>
            </div>
          </section>

          <section className={trades ? "hard-card p-5 order-2" : "hard-card p-5"} data-testid="job-programme-section">
            <SectionTag icon={Calendar}>{trades ? "Planned dates · day planning" : "Programme"}</SectionTag>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="label-tag">Start date</Label>
                <Input type="date" value={form.start_date} onChange={set("start_date")} className="mt-1.5 rounded-sm border-zinc-400" data-testid="job-start-input" />
                {trades && <label className="block mt-3 text-sm">Start time hint (optional)<Input type="time" value={form.start_time || ""} onChange={set("start_time")} className="mt-1" /></label>}
              </div>
              <div>
                <Label className="label-tag">End date</Label>
                <Input type="date" value={form.end_date} onChange={set("end_date")} className={`mt-1.5 rounded-sm ${dateError ? "border-red-500" : "border-zinc-400"}`} data-testid="job-end-input" />
                <Err msg={dateError ? "End date must be after the start date" : null} />
              </div>
            </div>
            {duration && <p className="text-[11px] font-mono uppercase tracking-widest text-zinc-500 mt-3" data-testid="job-duration">Duration: {duration} day{duration === 1 ? "" : "s"}</p>}
          </section>

          <CommercialSection className={trades ? "hard-card p-5 order-4" : "hard-card p-5"} data-testid="job-commercial-section">
            {trades ? <summary className="cursor-pointer font-semibold text-sm mb-3">Billing &amp; optional commercial details</summary> : <SectionTag icon={DollarSign}>Commercial</SectionTag>}
            <Label className="label-tag">Quoted value <span className="text-zinc-400 normal-case">— NZD, optional</span></Label>
            <div className="relative mt-1.5 max-w-xs">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 text-sm font-bold">$</span>
              <Input type="number" inputMode="decimal" min="0" value={form.quoted_value ?? ""} onChange={set("quoted_value")}
                placeholder="125,000" className="pl-7 rounded-sm border-zinc-400" data-testid="job-quoted-input" />
            </div>
            <p className="text-[11px] text-zinc-500 mt-2">Used for live job margin and project financial tracking.</p>

            <div className="mt-5 pt-5 border-t border-zinc-200 dark:border-white/10" data-testid="job-billing-full-section">
              <JobBillingSection value={form} onChange={(next) => { setForm((f) => ({ ...f, ...next })); setDirty(true); }} />
            </div>
          </CommercialSection>

          <section className={trades ? "hard-card p-5 order-1" : "hard-card p-5"} data-testid="job-scope-section">
            <SectionTag icon={MapPin}>{trades ? "Work required" : "Scope of works"}</SectionTag>
            <Textarea rows={7} value={form.description} onChange={set("description")}
              placeholder={trades ? "Describe the work required, site details and instructions…" : "Describe what needs to be completed on this job…\n\n• Excavate 18m trench along southern boundary\n• Install 450 RCRRJ culvert\n• Reinstate seal and line marking"}
              className="rounded-sm border-zinc-400" data-testid="job-scope-input" />
            <p className="text-[11px] text-zinc-500 mt-2">This scope is visible to crew members with access to the job.</p>
          </section>

          <section className={trades ? "hard-card p-5 order-3" : "hard-card p-5"} data-testid="job-crew-section" data-tour-id="job-crew-section">
            <div className="flex items-center justify-between mb-3">
              <SectionTag icon={Users}>{trades ? "Job access team" : "Assign crew"}</SectionTag>
              <span className="text-[11px] font-mono uppercase tracking-widest text-zinc-500" data-testid="crew-selected-count">{form.assigned_user_ids.length} selected</span>
            </div>
            {trades && <p className="mb-3 text-sm text-muted-foreground">Choose who can access the job. Use Schedule after saving to create dated worker assignments.</p>}
            <div className="flex gap-2 flex-wrap">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="w-4 h-4 text-zinc-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <Input value={crewQ} onChange={(e) => setCrewQ(e.target.value)} placeholder="Search team members…" className="pl-9 rounded-sm border-zinc-400" data-testid="crew-search-input" />
              </div>
              {[["all", "All"], ["field", "Field"], ["office", "Office"]].map(([v, l]) => (
                <button key={v} onClick={() => setCrewFilter(v)}
                  className={`text-[10px] font-bold uppercase tracking-wider border px-3 rounded-sm transition-colors ${crewFilter === v ? "border-[#FF5F15] text-[#FF5F15]" : "border-zinc-300 text-zinc-500 hover:text-zinc-800 dark:hover:text-white"}`}
                  data-testid={`crew-filter-${v}`}>{l}</button>
              ))}
            </div>
            <div className="mt-3 max-h-64 overflow-y-auto divide-y divide-zinc-200 dark:divide-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-sm">
              {crew.map((u) => (
                <label key={u.id} className="flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-zinc-50 dark:hover:bg-zinc-900">
                  <Checkbox checked={form.assigned_user_ids.includes(u.id)} onCheckedChange={() => toggleCrew(u.id)} data-testid={`assign-user-${u.id}`} />
                  <span className="w-8 h-8 rounded-full bg-[#09090B] dark:bg-zinc-800 text-white flex items-center justify-center text-[10px] font-black shrink-0">
                    {(u.name || "?").split(" ").map((x) => x[0]).slice(0, 2).join("").toUpperCase()}
                  </span>
                  <span className="min-w-0"><span className="block font-bold text-sm truncate">{u.name}</span>
                    <span className="block text-[10px] font-mono uppercase tracking-widest text-zinc-500">{u.role}</span></span>
                </label>
              ))}
              {crew.length === 0 && <div className="px-3 py-4 text-xs text-zinc-400 italic">No team members match.</div>}
            </div>
            {selectedUsers.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-1.5" data-testid="crew-selected-chips">
                {selectedUsers.map((u) => (
                  <span key={u.id} className="inline-flex items-center gap-1.5 text-[11px] font-bold border border-[#FF5F15]/50 text-[#FF5F15] px-2 py-1 rounded-sm">
                    {u.name}
                    <button onClick={() => toggleCrew(u.id)} aria-label={`Remove ${u.name}`} data-testid={`crew-chip-remove-${u.id}`}><X className="w-3 h-3" /></button>
                  </span>
                ))}
              </div>
            )}
          </section>
        </div>

        {/* RIGHT — live summary */}
        <aside className="hard-card p-5 lg:sticky lg:top-[76px]" data-testid="job-summary-panel">
          <div className="label-tag mb-3">Job summary</div>
          <div className="font-display font-black uppercase tracking-tight text-xl leading-tight">{form.title.trim() || "Untitled job"}</div>
          <div className="mt-2 inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-[#FF5F15]">
            <span className="w-1.5 h-1.5 rounded-full bg-[#FF5F15]" /> {STATUS_LABEL[form.status]}
          </div>
          <dl className="mt-4 space-y-3 text-sm">
            {[[trades ? "Customer" : "Client", clientName], ["Location", form.site_address],
              [trades ? "Planned dates" : "Programme", form.start_date && form.end_date ? `${form.start_date} → ${form.end_date}${duration ? ` · ${duration} days` : ""}` : null],
              [trades ? "Job access team" : "Crew", form.assigned_user_ids.length ? `${form.assigned_user_ids.length} assigned` : null],
              ["Quoted value", form.quoted_value ? money(form.quoted_value) : null]].map(([k, v]) => (
              <div key={k}>
                <dt className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">{k}</dt>
                <dd className={v ? "font-semibold" : "text-zinc-400 italic text-xs"}>{v || "Not set"}</dd>
              </div>
            ))}
            <div data-testid="job-summary-billing">
              <dt className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">Billing</dt>
              <dd className="font-semibold">
                {form.billing_type === "charge_up" ? "Charge-Up / Time & Materials" : form.billing_type === "internal" ? "Internal / Non-Billable" : "Fixed Price"}
              </dd>
              {jobBillingSummary(form) && (
                <div className="text-[11px] text-zinc-500 mt-0.5">
                  Bill: {jobBillingSummary(form).bill.join(", ") || "none"} · Invoice: {jobBillingSummary(form).freq}
                </div>
              )}
            </div>
          </dl>
          <div className="mt-5 border-t border-zinc-200 dark:border-zinc-700 pt-4">
            <div className="flex items-center justify-between text-[10px] font-bold uppercase tracking-widest text-zinc-500 mb-1.5">
              <span>Job setup</span><span data-testid="job-setup-pct">{pct}%</span>
            </div>
            <div className="h-1.5 bg-zinc-200 dark:bg-zinc-800 rounded-full overflow-hidden"><div className="h-full bg-[#FF5F15] transition-all" style={{ width: `${pct}%` }} /></div>
            <ul className="mt-3 space-y-1">
              {checklist.map(([label, ok]) => (
                <li key={label} className={`flex items-center gap-2 text-xs ${ok ? "text-zinc-700 dark:text-zinc-300" : "text-zinc-400"}`}>
                  {ok ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <span className="w-3.5 h-3.5 rounded-full border border-zinc-400 inline-block" />}
                  {label}
                </li>
              ))}
            </ul>
            <p className="text-[11px] text-zinc-500 mt-2">Only the job title is required — the rest can be added later.</p>
          </div>
          {!trades && <Button onClick={create} disabled={busy} className="mt-4 w-full rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white uppercase tracking-wider font-bold" data-testid="job-summary-create">
            {busy ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Creating job…</> : "Create job"}
          </Button>}
        </aside>
      </div>

      <Dialog open={leaveOpen} onOpenChange={setLeaveOpen}>
        <DialogContent className="rounded-xl max-w-md" data-testid="job-leave-dialog">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-tight text-xl">Discard new job?</DialogTitle>
            <DialogDescription>You have unsaved changes.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLeaveOpen(false)} className="rounded-sm uppercase tracking-wider font-bold" data-testid="job-keep-editing">Keep editing</Button>
            <Button onClick={() => nav("/app/jobs")} className="rounded-sm bg-red-600 hover:bg-red-700 text-white uppercase tracking-wider font-bold" data-testid="job-discard">Discard</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
